import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Phone, Loader2, CheckCircle2, Navigation, CalendarClock, Box, MapPin, AlertTriangle,
  PenLine, Eraser, X,
} from "lucide-react";
import { API, BRAND } from "../constants/brand";

const VEHICLE_NAMES = { transit: "Ford Transit Kasten", movano: "Opel Movano", crafter: "VW Crafter" };
const CARGO_LABELS = { express: "Express-Paket", pakete: "Pakete & Kartons", paletten: "Palettenware" };

const fmtWhen = (iso) => {
  if (!iso) return "Nach Absprache";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + " Uhr";
};

const cargoLine = (t) => {
  let s = CARGO_LABELS[t.cargo_type] || t.cargo_type || "";
  const count = t.cargo_type === "paletten" ? t.pallets : t.pieces;
  if (count) s += ` · ${count} ${t.cargo_type === "paletten" ? "Paletten" : "Stück"}`;
  if (t.dims) s += ` · ${t.dims}`;
  if (t.weight_kg) s += ` · ${t.weight_kg} kg`;
  return s || "—";
};

const STATUS_LABELS = { gebucht: "Gebucht", bestätigt: "Bestätigt", unterwegs: "Unterwegs", abgeschlossen: "Zugestellt" };

const SignatureModal = ({ tour, busy, onConfirm, onSkip, onClose }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B0F17";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (canvasRef.current.width / r.width),
      y: (e.clientY - r.top) * (canvasRef.current.height / r.height),
    };
  };
  const start = (e) => {
    drawing.current = true;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    e.target.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    dirty.current = false;
  };
  const confirm = () => {
    if (!dirty.current) {
      toast.error("Bitte zuerst unterschreiben lassen — oder ohne Unterschrift zustellen.");
      return;
    }
    onConfirm(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div data-testid="signature-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#121826] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white font-semibold text-sm">
            <PenLine size={16} className="text-cyan-400" /> Unterschrift Empfänger
          </div>
          <button data-testid="signature-close" onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-300"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-400">
          {tour.order_no} · Bitte den Empfänger hier mit dem Finger unterschreiben lassen:
        </p>
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          width={640}
          height={260}
          className="w-full rounded-xl touch-none cursor-crosshair border border-white/10"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        <div className="flex gap-2">
          <button
            data-testid="signature-clear"
            onClick={clear}
            disabled={!!busy}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-white/10 px-3 py-3 text-xs font-semibold text-slate-300 hover:border-white/25 transition-colors disabled:opacity-50"
          >
            <Eraser size={14} /> Löschen
          </button>
          <button
            data-testid="signature-confirm"
            onClick={confirm}
            disabled={!!busy}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 py-3 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Unterschrift bestätigen
          </button>
        </div>
        <button
          data-testid="signature-skip"
          onClick={onSkip}
          disabled={!!busy}
          className="w-full text-center text-[11px] text-slate-500 hover:text-slate-300 transition-colors py-1 disabled:opacity-50"
        >
          Ohne Unterschrift zustellen
        </button>
      </div>
    </div>
  );
};

export default function DriverPortal() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sigFor, setSigFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/fahrer/${token}/touren`);
      setData(res);
      setState("ok");
    } catch {
      setState("error");
    }
  }, [token]);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  const setStatus = async (rideId, status, signature = null) => {
    setBusy(rideId);
    try {
      await axios.post(`${API}/fahrer/${token}/touren/${rideId}/status`, {
        status,
        ...(signature ? { signature } : {}),
      });
      toast.success(status === "unterwegs" ? "Tour gestartet — Kunde per E-Mail informiert." : "Zugestellt gemeldet — Kunde informiert. Danke!");
      setSigFor(null);
      await load();
    } catch {
      toast.error("Status konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setBusy(null);
    }
  };

  const open = (data?.touren || []).filter((t) => t.status !== "abgeschlossen");
  const done = (data?.touren || []).filter((t) => t.status === "abgeschlossen");

  return (
    <div data-testid="fahrer-portal" className="min-h-screen bg-[#0A0E14] text-slate-200">
      <header className="border-b border-white/5 bg-[#0B0F17]/90 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/logo-mark.png" alt={`${BRAND.name} Logo`} className="w-9 h-9 rounded-lg object-cover" />
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.25em] text-cyan-400 font-mono-tech uppercase">Fahrer-Portal</div>
            {data && <div data-testid="fahrer-name" className="font-semibold text-white text-sm truncate">Hallo {data.driver.name}</div>}
          </div>
          {data?.driver?.vehicle && (
            <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono-tech text-slate-400 shrink-0">
              <Truck size={13} className="text-cyan-400/80" /> {VEHICLE_NAMES[data.driver.vehicle] || data.driver.vehicle}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6 space-y-4">
        {state === "loading" && (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>
        )}

        {state === "error" && (
          <div data-testid="fahrer-error" className="rounded-2xl border border-red-400/20 bg-red-400/5 p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-sm text-red-300">Ungültiger oder abgelaufener Link.</p>
            <p className="text-xs text-slate-500 mt-1">Bitte deinen Disponenten nach einem neuen Portal-Link fragen.</p>
          </div>
        )}

        {state === "ok" && (
          <>
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-bold text-white">Offene Touren ({open.length})</h1>
              <button data-testid="fahrer-refresh" onClick={load} className="text-[11px] font-mono-tech text-cyan-300 border border-cyan-400/25 rounded-full px-3 py-1.5 hover:bg-cyan-400/10 transition-colors">
                Aktualisieren
              </button>
            </div>

            {open.length === 0 && (
              <div data-testid="fahrer-empty" className="rounded-2xl border border-white/5 bg-white/[0.02] p-8 text-center">
                <CheckCircle2 className="w-9 h-9 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm text-slate-300">Keine offenen Touren.</p>
                <p className="text-xs text-slate-500 mt-1">Gute Fahrt!</p>
              </div>
            )}

            {open.map((t) => (
              <div key={t.id} data-testid={`tour-card-${t.id}`} className="rounded-2xl border border-white/8 bg-[#121826] p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-tech text-[10px] text-cyan-400/90 tracking-widest">{t.order_no}</span>
                  <span className={`text-[10px] font-mono-tech uppercase tracking-widest rounded-full border px-2.5 py-1 ${
                    t.status === "unterwegs"
                      ? "text-amber-300 border-amber-400/30 bg-amber-400/10"
                      : "text-sky-300 border-sky-400/30 bg-sky-400/10"
                  }`}>
                    {STATUS_LABELS[t.status] || t.status}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-cyan-300">
                  <CalendarClock size={16} className="shrink-0" />
                  <span className="text-base font-semibold text-white">{fmtWhen(t.scheduled_at)}</span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex gap-2.5">
                    <MapPin size={15} className="text-cyan-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Abholung</div>
                      <div className="text-slate-100">{t.pickup}</div>
                    </div>
                  </div>
                  <div className="flex gap-2.5">
                    <MapPin size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">Zustellung</div>
                      <div className="text-slate-100">{t.dropoff}</div>
                    </div>
                  </div>
                  <div className="flex gap-2.5">
                    <Box size={15} className="text-slate-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-slate-400">{cargoLine(t)}{t.distance_km ? ` · ${t.distance_km} km` : ""}</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <a
                    data-testid={`tour-call-${t.id}`}
                    href={`tel:${t.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] py-3 text-xs font-semibold text-slate-200 hover:border-cyan-400/30 transition-colors"
                  >
                    <Phone size={14} /> {t.name}
                  </a>
                  <a
                    data-testid={`tour-nav-${t.id}`}
                    href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(t.pickup)}&destination=${encodeURIComponent(t.dropoff)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] py-3 text-xs font-semibold text-slate-200 hover:border-cyan-400/30 transition-colors"
                  >
                    <Navigation size={14} /> Navigation
                  </a>
                </div>

                {(t.status === "gebucht" || t.status === "bestätigt") && (
                  <button
                    data-testid={`tour-start-${t.id}`}
                    onClick={() => setStatus(t.id, "unterwegs")}
                    disabled={busy === t.id}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-4 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {busy === t.id ? <Loader2 size={17} className="animate-spin" /> : <Truck size={17} />}
                    Tour starten
                  </button>
                )}
                {t.status === "unterwegs" && (
                  <button
                    data-testid={`tour-deliver-${t.id}`}
                    onClick={() => setSigFor(t.id)}
                    disabled={busy === t.id}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 py-4 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    <CheckCircle2 size={17} />
                    Zugestellt melden
                  </button>
                )}
              </div>
            ))}

            {done.length > 0 && (
              <div data-testid="fahrer-done" className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-tech mb-2">Erledigt heute / zuletzt ({done.length})</div>
                <div className="space-y-2">
                  {done.map((t) => (
                    <div key={t.id} data-testid={`tour-done-${t.id}`} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                      <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-mono-tech text-[10px] text-slate-500">{t.order_no}</div>
                        <div className="text-xs text-slate-400 truncate">{t.pickup} → {t.dropoff}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {sigFor && (
        <SignatureModal
          tour={(data?.touren || []).find((t) => t.id === sigFor) || {}}
          busy={busy === sigFor}
          onConfirm={(sig) => setStatus(sigFor, "abgeschlossen", sig)}
          onSkip={() => setStatus(sigFor, "abgeschlossen", null)}
          onClose={() => setSigFor(null)}
        />
      )}
    </div>
  );
}
